import { useCallback, useEffect, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Crosshair } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface MapPickerProps {
  value: { lat: number; lng: number } | null;
  onChange: (coords: { lat: number; lng: number }) => void;
  height?: number;
}

const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) ?? '';

export function MapPicker({ value, onChange, height = 320 }: MapPickerProps) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'gmaps-script',
    googleMapsApiKey: apiKey,
  });

  const [center, setCenter] = useState({ lat: value?.lat ?? 25.0, lng: value?.lng ?? 35.0 });

  useEffect(() => {
    if (value) setCenter({ lat: value.lat, lng: value.lng });
  }, [value]);

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      }
    },
    [onChange],
  );

  const useBrowserGeo = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {/* user denied or unavailable */},
      { enableHighAccuracy: true, timeout: 5_000 },
    );
  }, [onChange]);

  if (!apiKey) {
    return (
      <div className="grid place-items-center rounded-md border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--muted-foreground)]" style={{ height }}>
        Karten-Picker benötigt <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>.env.local</code>.
      </div>
    );
  }
  if (loadError) {
    return <div className="text-sm text-[var(--destructive)]">Karten konnten nicht geladen werden.</div>;
  }
  if (!isLoaded) {
    return <div className="grid place-items-center" style={{ height }}><Spinner className="h-6 w-6" /></div>;
  }

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-md border border-[var(--border)]">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height }}
          center={center}
          zoom={value ? 10 : 3}
          onClick={onMapClick}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {value && <Marker position={{ lat: value.lat, lng: value.lng }} />}
        </GoogleMap>
      </div>
      <div className="flex items-center justify-between text-xs text-[var(--muted-foreground)]">
        <span>{value ? `${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}` : 'Klicke auf die Karte um Position zu wählen.'}</span>
        <Button type="button" variant="ghost" size="sm" onClick={useBrowserGeo}>
          <Crosshair className="h-4 w-4" /> Meine Position
        </Button>
      </div>
    </div>
  );
}
